import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  Input,
  Select,
  Skeleton,
  Slider,
  Tag,
  Tooltip,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import * as d3 from "d3-force";

import { apiFetchJson } from "@/utils/apiFetch";
import { DECISION_COLOR, SectionCard, fmtNum, useMasteryApi } from "./shared";
import { connectedSiteIds, visibleMatchEdges } from "./masteryGraph.utils";

// Mastering graph: scoped force layout over scored pairs, ported from the
// standalone Golden Explorer's GraphView. Nodes are normalized sites, edges
// are scored pairs, connected components over accepted edges are the clusters
// mastering collapses into golden records. Scope via seed/pair props or the
// region/country/state/city/phase facets. Server caps the scope at 300 nodes.

// ── payload types (see views_portfolio_mastery_graph.py) ─────────────────────

interface SiteNode {
  id: string;
  name: string | null;
  city: string | null;
  state: string | null;
  golden_id: string | null;
  golden_name: string | null;
  org: string | null;
  cluster: string;
}
interface GraphEdge {
  a: string;
  b: string;
  conf: number | null;
  status: string;
}
interface PiNode {
  id: string;
  name: string | null;
  sites: number;
}
interface OrgNode {
  id: string;
  name: string | null;
  org_type: string | null;
  tier: string | null;
  sites: number;
}
interface GraphPayload {
  site_nodes: SiteNode[];
  edges: GraphEdge[];
  truncated?: boolean;
  error?: string;
  pi_nodes?: PiNode[];
  pi_edges?: Array<{ pi: string; site: string }>;
  org_nodes?: OrgNode[];
  org_edges?: Array<{ org: string; site: string }>;
}
interface ScopesPayload {
  regions: string[];
  countries: Array<{ code: string; n: number }>;
  states: Array<{ state: string }>;
  cities: Array<{ city: string }>;
  phases: string[];
}

// ── colors ────────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  auto_accepted: DECISION_COLOR.accept,
  human_review: DECISION_COLOR.review,
  auto_rejected: DECISION_COLOR.reject,
};
const PI_COLOR = "#7C3AED";
const ORG_COLOR = "#0891B2";
const PRIMARY = "#0C98D0"; // Vivo primary (theme token)
// Categorical palette for the distinct golden records inside a selected
// cluster (one hue per golden_id, most-common records first) — inlined from
// the Golden Explorer's clusterPalette.
const CLUSTER_PALETTE = [
  "#2563EB",
  "#DC2626",
  "#16A34A",
  "#D97706",
  "#DB2777",
  "#4F46E5",
  "#CA8A04",
  "#059669",
  "#7C3AED",
  "#0891B2",
];

const W = 1100;
const H = 620;
const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

type XY = { x: number; y: number };
type Hover = {
  label: string;
  sub?: string;
  kind: string;
  x: number;
  y: number;
} | null;

export default function MasteryGraph({
  seed,
  pair,
}: {
  seed?: string;
  pair?: string;
}) {
  const scoped = !!(seed || pair);
  // Location scope selectors. The default is the whole tenant (capped
  // server-side) rather than a hard-coded country: defaulting to US showed
  // Clario 4 accepted edges out of the 314 sites it has, which reads as a
  // graph that ignores its filters.
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [stateProv, setStateProv] = useState("");
  const [city, setCity] = useState("");
  const [phase, setPhase] = useState("");
  const [q, setQ] = useState("");
  const [showRejected, setShowRejected] = useState(false);
  const [showPI, setShowPI] = useState(false);
  const [showOrg, setShowOrg] = useState(false);
  const [minConf, setMinConf] = useState(0);
  // seed/pair scopes are often small (even singletons) — show every scoped
  // node so the canvas never renders blank. Broad scopes hide isolated nodes.
  const [hideIsolated, setHideIsolated] = useState(!scoped);

  const facets = scoped
    ? {}
    : {
        region: region || undefined,
        country: country || undefined,
        state: stateProv || undefined,
        city: city || undefined,
        phase: phase || undefined,
        q: q || undefined,
      };
  // With no facet chosen, ask for the whole tenant explicitly — the endpoint
  // requires a scope and would otherwise answer with "provide a scope".
  const anyFacet = Object.values(facets).some(Boolean);
  const { data, isLoading } = useMasteryApi<GraphPayload>("mastery-graph/", {
    seed,
    pair,
    ...facets,
    all: !scoped && !anyFacet ? true : undefined,
    include_rejected: showRejected || undefined,
    include_pi: showPI || undefined,
    include_org: showOrg || undefined,
  });

  // cascading option lists for the selectors (skipped when seed/pair-scoped)
  const scopesQs = [
    country && `country=${encodeURIComponent(country)}`,
    stateProv && `state=${encodeURIComponent(stateProv)}`,
  ]
    .filter(Boolean)
    .join("&");
  const { data: scopes } = useQuery({
    queryKey: ["mastery-graph-scopes", scopesQs],
    queryFn: () =>
      apiFetchJson<ScopesPayload>(
        `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-graph/scopes/${scopesQs ? `?${scopesQs}` : ""}`,
      ),
    staleTime: 60_000,
    enabled: !scoped,
  });
  const opts = (arr: string[] | undefined) =>
    (arr || []).map((v) => ({ value: v, label: v }));
  // A facet the tenant has no values for is disabled and says so. Rendering
  // an enabled, empty picker (Clario ships no city or state on any of its 314
  // normalized sites) is indistinguishable from a filter that is broken.
  const regionOpts = opts(scopes?.regions);
  const stateOpts = opts((scopes?.states || []).map((s) => s.state));
  const cityOpts = opts((scopes?.cities || []).map((c) => c.city));
  const phaseOpts = opts(scopes?.phases);

  const [positions, setPositions] = useState<Record<string, XY>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [hover, setHover] = useState<Hover>(null);
  const [tf, setTf] = useState({ x: 0, y: 0, k: 1 });

  const svgRef = useRef<SVGSVGElement | null>(null);
  const panRef = useRef<{
    ox: number;
    oy: number;
    tx: number;
    ty: number;
    moved: boolean;
  } | null>(null);

  const filteredEdges = useMemo(
    () => visibleMatchEdges(data?.edges, { showRejected, minConf }),
    [data, showRejected, minConf],
  );

  // Visible sites: those with a shown connection, or (when not hiding
  // isolated) all. Overlay links count — see connectedSiteIds.
  const connected = useMemo(
    () => connectedSiteIds(filteredEdges, data?.pi_edges, data?.org_edges),
    [data, filteredEdges],
  );
  const visibleNodes = useMemo(
    () =>
      (data?.site_nodes || []).filter(
        (n) => !hideIsolated || connected.has(n.id),
      ),
    [data, hideIsolated, connected],
  );
  const visibleSiteIds = useMemo(
    () => new Set(visibleNodes.map((n) => n.id)),
    [visibleNodes],
  );

  // PI / org overlays — only where the site endpoint is currently visible.
  const piEdges = useMemo(() => {
    // dedupe (pi, site) so identical assignment rows don't stack lines
    const seen = new Set<string>();
    return (data?.pi_edges || []).filter((e) => {
      const k = `${e.pi}|${e.site}`;
      if (!visibleSiteIds.has(e.site) || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [data, visibleSiteIds]);
  const visiblePIs = useMemo(() => {
    const on = new Set(piEdges.map((e) => e.pi));
    return (data?.pi_nodes || []).filter((n) => on.has(n.id));
  }, [data, piEdges]);
  const orgEdges = useMemo(() => {
    const seen = new Set<string>();
    return (data?.org_edges || []).filter((e) => {
      const k = `${e.org}|${e.site}`;
      if (!visibleSiteIds.has(e.site) || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [data, visibleSiteIds]);
  const visibleOrgs = useMemo(() => {
    const on = new Set(orgEdges.map((e) => e.org));
    return (data?.org_nodes || []).filter((n) => on.has(n.id));
  }, [data, orgEdges]);

  // One force layout over sites + (optional) PI + org nodes; ids prefixed to
  // avoid collisions between the node families.
  useEffect(() => {
    if (!data?.site_nodes?.length) return;
    type SimNode = d3.SimulationNodeDatum & {
      key: string;
      _pi?: boolean;
      _org?: boolean;
    };
    type SimLink = { source: string; target: string; conf: number };
    const nodes: SimNode[] = data.site_nodes.map((n) => ({ key: n.id }));
    (data.pi_nodes || []).forEach((p) =>
      nodes.push({ key: `pi:${p.id}`, _pi: true }),
    );
    (data.org_nodes || []).forEach((o) =>
      nodes.push({ key: `org:${o.id}`, _org: true }),
    );
    const links: SimLink[] = (data.edges || [])
      .filter((e) => e.status !== "auto_rejected")
      .map((e) => ({ source: e.a, target: e.b, conf: e.conf ?? 0.5 }));
    (data.pi_edges || []).forEach((e) =>
      links.push({ source: `pi:${e.pi}`, target: e.site, conf: 0.5 }),
    );
    (data.org_edges || []).forEach((e) =>
      links.push({ source: `org:${e.org}`, target: e.site, conf: 0.5 }),
    );

    const sim = d3
      .forceSimulation(nodes)
      .force(
        "link",
        d3
          .forceLink<
            SimNode,
            d3.SimulationLinkDatum<SimNode> & { conf: number }
          >(links)
          .id((d) => d.key)
          .distance((l) => 30 + (1 - (l.conf || 0.5)) * 40)
          .strength(0.7),
      )
      .force("charge", d3.forceManyBody().strength(-70))
      .force("center", d3.forceCenter(W / 2, H / 2))
      .force("x", d3.forceX(W / 2).strength(0.06))
      .force("y", d3.forceY(H / 2).strength(0.06))
      .force(
        "collide",
        d3.forceCollide<SimNode>((d) => (d._org ? 16 : 12)),
      )
      .stop();
    for (let i = 0; i < 300; i++) sim.tick();
    const pos: Record<string, XY> = {};
    nodes.forEach((n) => {
      pos[n.key] = { x: n.x ?? 0, y: n.y ?? 0 };
    });
    // Layout is computed synchronously from the fetched graph (ticked
    // simulation) — same pattern as TabsSwitcher.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setPositions(pos);

    // Auto-fit the whole layout into the viewBox so nothing spills off-canvas.
    const xs = nodes.map((n) => n.x ?? 0);
    const ys = nodes.map((n) => n.y ?? 0);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = 50;
    const bw = Math.max(maxX - minX, 1);
    const bh = Math.max(maxY - minY, 1);
    const k = clamp(Math.min((W - 2 * pad) / bw, (H - 2 * pad) / bh), 0.2, 3);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- see above
    setTf({ k, x: W / 2 - k * cx, y: H / 2 - k * cy });
  }, [data]);

  // Wheel-zoom toward the cursor (native, non-passive so we can stop page
  // scroll).
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = svg.getBoundingClientRect();
      const mx = ((e.clientX - r.left) / r.width) * W;
      const my = ((e.clientY - r.top) / r.height) * H;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      setTf((t) => {
        const k = clamp(t.k * factor, 0.1, 8);
        const kf = k / t.k;
        return { k, x: mx - (mx - t.x) * kf, y: my - (my - t.y) * kf };
      });
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
    // re-attach once the <svg> actually mounts (it renders only after data
    // loads, so an empty dep array would bind before the element exists)
  }, [isLoading, data]);

  const toView = useCallback((clientX: number, clientY: number) => {
    const r = svgRef.current!.getBoundingClientRect();
    return {
      x: ((clientX - r.left) / r.width) * W,
      y: ((clientY - r.top) / r.height) * H,
    };
  }, []);
  const onBgDown = (e: React.MouseEvent) => {
    const v = toView(e.clientX, e.clientY);
    panRef.current = { ox: v.x, oy: v.y, tx: tf.x, ty: tf.y, moved: false };
  };
  const onBgMove = (e: React.MouseEvent) => {
    if (!panRef.current) return;
    const v = toView(e.clientX, e.clientY);
    const dx = v.x - panRef.current.ox;
    const dy = v.y - panRef.current.oy;
    if (Math.abs(dx) + Math.abs(dy) > 2) panRef.current.moved = true;
    setTf((t) => ({
      ...t,
      x: panRef.current!.tx + dx,
      y: panRef.current!.ty + dy,
    }));
  };
  const onBgUp = () => {
    panRef.current = null;
  };
  const zoomBtn = (factor: number) =>
    setTf((t) => {
      const k = clamp(t.k * factor, 0.1, 8);
      const kf = k / t.k;
      return {
        k,
        x: W / 2 - (W / 2 - t.x) * kf,
        y: H / 2 - (H / 2 - t.y) * kf,
      };
    });
  const resetView = () => {
    // recompute fit from current positions
    const keys = Object.keys(positions);
    if (!keys.length) return setTf({ x: 0, y: 0, k: 1 });
    const xs = keys.map((kk) => positions[kk].x);
    const ys = keys.map((kk) => positions[kk].y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = 50;
    const bw = Math.max(maxX - minX, 1);
    const bh = Math.max(maxY - minY, 1);
    const k = clamp(Math.min((W - 2 * pad) / bw, (H - 2 * pad) / bh), 0.2, 3);
    setTf({
      k,
      x: W / 2 - (k * (minX + maxX)) / 2,
      y: H / 2 - (k * (minY + maxY)) / 2,
    });
  };

  const cluster = selected
    ? (data?.site_nodes || []).find((n) => n.id === selected)?.cluster
    : null;
  // when a cluster is selected, give each distinct golden record in it its own
  // hue (most-common record first) so a split cluster shows its composition
  const goldenColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (!cluster) return map;
    const counts = new Map<string, number>();
    (data?.site_nodes || []).forEach((n) => {
      if (n.cluster === cluster && n.golden_id)
        counts.set(n.golden_id, (counts.get(n.golden_id) || 0) + 1);
    });
    [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .forEach(([gid], i) => {
        map[gid] = CLUSTER_PALETTE[i % CLUSTER_PALETTE.length];
      });
    return map;
  }, [cluster, data]);
  const multiGolden = Object.keys(goldenColorMap).length > 1;
  const gt = `translate(${tf.x},${tf.y}) scale(${tf.k})`;
  const enterHover = (
    h: Omit<NonNullable<Hover>, "x" | "y">,
    e: React.MouseEvent,
  ) => setHover({ ...h, x: e.clientX, y: e.clientY });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <span className="font-mcqueenGrotesk text-sm font-bold text-[#2B4752]">
          Match graph{" "}
          <Tooltip title="Nodes are normalized site records, edges are scored pairs, and connected clusters are the golden records mastering collapses them into.">
            <InfoCircleOutlined className="ml-1 text-xs text-[#556C75]" />
          </Tooltip>
        </span>
        {data?.truncated && (
          <Tag color="#D97706" className="mr-0">
            scope capped at 300 nodes
          </Tag>
        )}
      </div>

      {/* scope + display controls */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-[#E6EEF2] bg-white p-4 shadow-sm">
        {!scoped && (
          <>
            <span className="text-sm font-medium text-[#556C75]">Scope:</span>
            <Select
              allowClear
              showSearch
              disabled={regionOpts.length === 0}
              placeholder={regionOpts.length ? "Region" : "Region — no data"}
              style={{ width: 170 }}
              value={region || undefined}
              onChange={(v) => setRegion(v || "")}
              options={regionOpts}
            />
            <Select
              allowClear
              showSearch
              placeholder="Country"
              style={{ width: 130 }}
              value={country || undefined}
              onChange={(v) => {
                setCountry(v || "");
                setStateProv("");
                setCity("");
              }}
              options={(scopes?.countries || []).map((c) => ({
                value: c.code,
                label: `${c.code} (${c.n})`,
              }))}
            />
            <Select
              allowClear
              showSearch
              disabled={stateOpts.length === 0}
              placeholder={
                stateOpts.length ? "State/Province" : "State/Province — no data"
              }
              style={{ width: 190 }}
              value={stateProv || undefined}
              onChange={(v) => {
                setStateProv(v || "");
                setCity("");
              }}
              options={stateOpts}
            />
            <Select
              allowClear
              showSearch
              disabled={cityOpts.length === 0}
              placeholder={cityOpts.length ? "City" : "City — no data"}
              style={{ width: 170 }}
              value={city || undefined}
              onChange={(v) => setCity(v || "")}
              options={cityOpts}
            />
            <Select
              allowClear
              showSearch
              disabled={phaseOpts.length === 0}
              placeholder={phaseOpts.length ? "Phase" : "Phase — no data"}
              style={{ width: 150 }}
              value={phase || undefined}
              onChange={(v) => setPhase(v || "")}
              options={phaseOpts}
            />
            <Input.Search
              placeholder="Site name contains…"
              style={{ width: 200 }}
              defaultValue={q}
              onSearch={setQ}
              allowClear
            />
          </>
        )}
        {scoped && (
          <span className="text-sm text-[#2B4752]">
            Scoped to <span className="font-semibold">{seed || pair}</span> +
            1-hop neighborhood
          </span>
        )}
        <Checkbox
          checked={showRejected}
          onChange={(e) => setShowRejected(e.target.checked)}
        >
          show rejected edges
        </Checkbox>
        <Checkbox
          checked={hideIsolated}
          onChange={(e) => setHideIsolated(e.target.checked)}
        >
          hide isolated
        </Checkbox>
        <Checkbox
          checked={showPI}
          onChange={(e) => setShowPI(e.target.checked)}
        >
          <span style={{ color: PI_COLOR }}>◆</span> show PIs
        </Checkbox>
        <Checkbox
          checked={showOrg}
          onChange={(e) => setShowOrg(e.target.checked)}
        >
          <span style={{ color: ORG_COLOR }}>■</span> show golden orgs
        </Checkbox>
        {/* The threshold and its effect are both shown: with only a handful of
            distinct pair confidences in a tenant, a bare slider looks inert
            even while it is filtering correctly. */}
        <span className="text-sm text-[#556C75]">
          min confidence{" "}
          <span className="font-semibold tabular-nums text-[#2B4752]">
            {minConf.toFixed(2)}
          </span>
        </span>
        <Slider
          min={0}
          max={1}
          step={0.05}
          value={minConf}
          onChange={setMinConf}
          style={{ width: 140 }}
        />
        <span className="text-xs tabular-nums text-[#556C75]">
          {fmtNum(filteredEdges.length)} of {fmtNum(data?.edges?.length ?? 0)}{" "}
          edges
        </span>
        {selected && (
          <Button size="small" onClick={() => setSelected(null)}>
            deselect
          </Button>
        )}
      </div>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : !data?.site_nodes?.length ? (
        <div className="rounded-lg border border-[#E6EEF2] bg-white p-8 text-center text-sm text-[#2B4752] shadow-sm">
          {data?.error ||
            "No sites in scope — widen or clear the scope filters, search by site name, or open the graph from a golden record."}
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_300px] gap-4">
          <div className="relative rounded-lg border border-[#E6EEF2] bg-white shadow-sm">
            <div className="absolute right-2 top-2 z-[2] flex gap-1">
              <Button size="small" onClick={() => zoomBtn(1.3)}>
                +
              </Button>
              <Button size="small" onClick={() => zoomBtn(1 / 1.3)}>
                −
              </Button>
              <Button size="small" onClick={resetView}>
                fit
              </Button>
            </div>
            <svg
              ref={svgRef}
              width="100%"
              viewBox={`0 0 ${W} ${H}`}
              style={{
                display: "block",
                cursor: panRef.current ? "grabbing" : "grab",
                touchAction: "none",
              }}
              onMouseDown={onBgDown}
              onMouseMove={onBgMove}
              onMouseUp={onBgUp}
              onMouseLeave={() => {
                onBgUp();
                setHover(null);
              }}
            >
              <g transform={gt}>
                {filteredEdges.map((e) => {
                  const pa = positions[e.a];
                  const pb = positions[e.b];
                  if (
                    !pa ||
                    !pb ||
                    !visibleSiteIds.has(e.a) ||
                    !visibleSiteIds.has(e.b)
                  )
                    return null;
                  const na = (data.site_nodes || []).find((n) => n.id === e.a);
                  const nb = (data.site_nodes || []).find((n) => n.id === e.b);
                  const inCluster =
                    cluster &&
                    (na?.cluster === cluster || nb?.cluster === cluster);
                  return (
                    <line
                      key={`${e.a}|${e.b}`}
                      x1={pa.x}
                      y1={pa.y}
                      x2={pb.x}
                      y2={pb.y}
                      stroke={STATUS_COLOR[e.status] || "#94a3b8"}
                      strokeWidth={0.8 + (e.conf || 0) * 3}
                      vectorEffect="non-scaling-stroke"
                      strokeOpacity={cluster ? (inCluster ? 0.9 : 0.08) : 0.55}
                    />
                  );
                })}
                {orgEdges.map((e) => {
                  const pa = positions[`org:${e.org}`];
                  const pb = positions[e.site];
                  if (!pa || !pb) return null;
                  return (
                    <line
                      key={`o|${e.org}|${e.site}`}
                      x1={pa.x}
                      y1={pa.y}
                      x2={pb.x}
                      y2={pb.y}
                      stroke={ORG_COLOR}
                      strokeWidth={1.5}
                      strokeOpacity={0.6}
                      strokeDasharray="4 3"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
                {piEdges.map((e) => {
                  const pa = positions[`pi:${e.pi}`];
                  const pb = positions[e.site];
                  if (!pa || !pb) return null;
                  return (
                    <line
                      key={`p|${e.pi}|${e.site}`}
                      x1={pa.x}
                      y1={pa.y}
                      x2={pb.x}
                      y2={pb.y}
                      stroke={PI_COLOR}
                      strokeWidth={1.5}
                      strokeOpacity={0.55}
                      strokeDasharray="3 3"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
                {visibleOrgs.map((o) => {
                  const p = positions[`org:${o.id}`];
                  if (!p) return null;
                  const s = 6 + Math.min(o.sites || 1, 6);
                  return (
                    <rect
                      key={`org:${o.id}`}
                      x={p.x - s}
                      y={p.y - s}
                      width={s * 2}
                      height={s * 2}
                      rx={2}
                      fill={ORG_COLOR}
                      stroke="#fff"
                      strokeWidth={1}
                      opacity={cluster ? 0.35 : 0.9}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(ev) =>
                        enterHover(
                          {
                            label: o.name || o.id,
                            sub: `golden org · ${fmtNum(o.sites)} sites${o.org_type ? ` · ${o.org_type}` : ""}`,
                            kind: "org",
                          },
                          ev,
                        )
                      }
                      onMouseLeave={() => setHover(null)}
                    />
                  );
                })}
                {visiblePIs.map((pi) => {
                  const p = positions[`pi:${pi.id}`];
                  if (!p) return null;
                  const s = 6;
                  return (
                    <rect
                      key={`pi:${pi.id}`}
                      x={-s}
                      y={-s}
                      width={s * 2}
                      height={s * 2}
                      transform={`translate(${p.x},${p.y}) rotate(45)`}
                      fill={PI_COLOR}
                      stroke="#fff"
                      strokeWidth={1}
                      opacity={cluster ? 0.35 : 0.9}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(ev) =>
                        enterHover(
                          {
                            label: pi.name || pi.id,
                            sub: `PI · ${fmtNum(pi.sites)} site link${pi.sites === 1 ? "" : "s"}`,
                            kind: "pi",
                          },
                          ev,
                        )
                      }
                      onMouseLeave={() => setHover(null)}
                    />
                  );
                })}
                {visibleNodes.map((n) => {
                  const p = positions[n.id];
                  if (!p) return null;
                  const inCluster = cluster && n.cluster === cluster;
                  // recolor by golden record only inside a selected
                  // multi-record cluster
                  const fill =
                    inCluster && multiGolden && n.golden_id
                      ? goldenColorMap[n.golden_id]
                      : n.golden_id
                        ? PRIMARY
                        : "#94a3b8";
                  return (
                    <g
                      key={n.id}
                      transform={`translate(${p.x},${p.y})`}
                      style={{ cursor: "pointer" }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() =>
                        setSelected(n.id === selected ? null : n.id)
                      }
                      onMouseEnter={(ev) =>
                        enterHover(
                          {
                            label: n.name || n.id,
                            sub: `${n.city || "?"}${n.org ? ` · ${n.org}` : ""}`,
                            kind: "site",
                          },
                          ev,
                        )
                      }
                      onMouseMove={(ev) =>
                        setHover((h) =>
                          h ? { ...h, x: ev.clientX, y: ev.clientY } : h,
                        )
                      }
                      onMouseLeave={() => setHover(null)}
                    >
                      <circle
                        r={n.id === selected ? 9 : 6}
                        fill={fill}
                        stroke={inCluster ? "#B45309" : "#fff"}
                        strokeWidth={inCluster ? 2.5 : 1}
                        opacity={cluster ? (inCluster ? 1 : 0.25) : 0.9}
                      />
                    </g>
                  );
                })}
              </g>
            </svg>
            {hover && (
              <div
                style={{
                  position: "fixed",
                  left: hover.x + 14,
                  top: hover.y + 14,
                  zIndex: 10,
                  pointerEvents: "none",
                  background: "rgba(15,23,42,0.94)",
                  color: "#fff",
                  padding: "6px 9px",
                  borderRadius: 6,
                  fontSize: 12,
                  maxWidth: 260,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                }}
              >
                <div style={{ fontWeight: 600 }}>{hover.label}</div>
                {hover.sub && (
                  <div style={{ opacity: 0.8, marginTop: 2 }}>{hover.sub}</div>
                )}
              </div>
            )}
          </div>

          <div>
            <SectionCard title="Legend & counts">
              <div className="text-sm text-[#2B4752]">
                <div className="mt-1">
                  <span style={{ color: DECISION_COLOR.accept }}>■</span>{" "}
                  accepted edge (
                  {fmtNum(
                    (data.edges || []).filter(
                      (e) => e.status === "auto_accepted",
                    ).length,
                  )}
                  )
                </div>
                <div className="mt-1">
                  <span style={{ color: DECISION_COLOR.review }}>■</span> review
                  edge (
                  {fmtNum(
                    (data.edges || []).filter(
                      (e) => e.status === "human_review",
                    ).length,
                  )}
                  )
                </div>
                <div className="mt-1">
                  <span style={{ color: DECISION_COLOR.reject }}>■</span>{" "}
                  rejected (
                  {fmtNum(
                    (data.edges || []).filter(
                      (e) => e.status === "auto_rejected",
                    ).length,
                  )}
                  , hidden by default)
                </div>
                <div className="mt-1">
                  <span style={{ color: PRIMARY }}>●</span> site ·{" "}
                  <span style={{ color: "#94a3b8" }}>●</span> ungrouped site
                </div>
                {showPI && (
                  <div className="mt-1">
                    <span style={{ color: PI_COLOR }}>◆</span> PI (
                    {fmtNum(visiblePIs.length)})
                  </div>
                )}
                {showOrg && (
                  <div className="mt-1">
                    <span style={{ color: ORG_COLOR }}>■</span> golden org (
                    {fmtNum(visibleOrgs.length)})
                  </div>
                )}
                <div className="mt-2">
                  {fmtNum(visibleNodes.length)} sites shown of{" "}
                  {fmtNum(data.site_nodes.length)} in scope
                  {hideIsolated ? " · isolated hidden" : ""}
                </div>
                <div className="mt-1">scroll to zoom · drag to pan</div>
              </div>
            </SectionCard>
            {selected &&
              (() => {
                const n = data.site_nodes.find((x) => x.id === selected);
                if (!n) return null;
                const members = data.site_nodes.filter(
                  (x) => x.cluster === n.cluster,
                );
                // group the cluster's nodes by the golden record they were
                // mastered into
                const byGolden = new Map<
                  string,
                  { id: string; name: string; nodes: number }
                >();
                let ungrouped = 0;
                members.forEach((m) => {
                  if (!m.golden_id) {
                    ungrouped += 1;
                    return;
                  }
                  const g = byGolden.get(m.golden_id) || {
                    id: m.golden_id,
                    name: m.golden_name || m.name || m.golden_id,
                    nodes: 0,
                  };
                  g.nodes += 1;
                  byGolden.set(m.golden_id, g);
                });
                const goldens = [...byGolden.values()].sort(
                  (a, b) => b.nodes - a.nodes,
                );
                const multi = goldens.length > 1;
                return (
                  <SectionCard title="Selected">
                    <div className="text-sm font-semibold text-[#2B4752]">
                      {n.name || n.id}
                    </div>
                    <div className="text-sm text-[#556C75]">
                      {n.city || "—"} {n.org ? `· ${n.org}` : ""}
                    </div>
                    <div className="mt-2 text-sm text-[#2B4752]">
                      {n.golden_id ? (
                        <>
                          golden record:{" "}
                          <span className="font-semibold">{n.golden_id}</span>
                        </>
                      ) : (
                        "not mastered into a golden record"
                      )}
                    </div>

                    <div className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-[#556C75]">
                      Cluster neighborhood · {members.length} nodes
                    </div>
                    {multi ? (
                      <div className="my-1 mb-2 rounded-md border border-[#F0C36D] bg-[#FEF3C7] px-2.5 py-2 text-sm text-[#2B4752]">
                        ⚠ This cluster spans{" "}
                        <b>{goldens.length} golden records</b>. The scorer links
                        these nodes, but mastering kept them as separate records
                        — they may be split duplicates.
                      </div>
                    ) : (
                      <div className="mb-2 text-sm text-[#556C75]">
                        all nodes belong to one golden record
                      </div>
                    )}
                    {goldens.map((g) => (
                      <div
                        key={g.id}
                        className="flex items-center justify-between py-0.5"
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span
                            className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                            style={{
                              background: multi
                                ? goldenColorMap[g.id]
                                : PRIMARY,
                            }}
                          />
                          <span
                            className={`max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap text-sm text-[#2B4752] ${g.id === n.golden_id ? "font-semibold" : ""}`}
                          >
                            {g.name}
                          </span>
                        </span>
                        <span className="text-sm tabular-nums text-[#2B4752]">
                          {g.nodes} node{g.nodes === 1 ? "" : "s"}
                          {g.id === n.golden_id ? " · selected" : ""}
                        </span>
                      </div>
                    ))}
                    {ungrouped > 0 && (
                      <div className="flex items-center justify-between py-0.5 text-sm text-[#556C75]">
                        <span>ungrouped (no golden record)</span>
                        <span className="tabular-nums">{ungrouped}</span>
                      </div>
                    )}
                  </SectionCard>
                );
              })()}
          </div>
        </div>
      )}
    </div>
  );
}
